import React, { useCallback, useEffect, useRef, useState } from "react";

import CourseAlumniTestimonials from "../../components/online-page/courseAlumniTestimonials";
import CourseContact from "../../components/online-page/courseContact";
import CourseCurriculum from "../../components/online-page/courseCurriculum";
import CourseExpertList from "../../components/online-page/courseExpertList";
import CourseSection from "../../components/online-page/courseSection";
import OnlineHeroContent from "../../components/online-page/onlineHeroContent";
import { VIHero } from "../../components/virtual-internship/viHero";
import { VIPartnerShip } from "../../components/virtual-internship/viPartnership";
import { useProgramTestimonials } from "../../hooks/useProgramTestimonials";
import { sortByDesc } from "../../utils/commonFunctions";
import CourseStickyBar from "../shared/sticky/courseStickyBar";

const OnlineCoursePageComponent = ({
  page,
  genericDetails,
  type,
  theme,
  applyNow,
  enquireNow
}) => {
  const curriculumRef = useRef(null);
  const stickyBarRef = useRef(null);
  const { currentSiteMeta } = theme;

  const [coursePrice, setCoursePrice] = useState({
    price: "",
    duration: "",
    durationDays: "",
    instanceDate: "",
    timeHours: "-"
  });

  const [nextCourseDate, setNextCourseDate] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      if (stickyBarRef.current) {
        const top = curriculumRef.current.getBoundingClientRect().top;
        if (top >= window.innerHeight) {
          stickyBarRef.current.style.bottom = "-168px";
        } else {
          stickyBarRef.current.style.bottom = "0";
        }
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
  }, []);

  const {
    title: pageTitle,
    acfOnlineCourses: pageDetails,
    content
  } = page || {};

  const {
    programCode,
    courseBrochure,
    courseIntroduction,
    courseIntroductionContent,
    courseCertificateDescription,
    courseInstructors,
    curriculumModules,
    courseBundleRelations
  } = pageDetails ?? {};

  const isBushwiseProgram = programCode?.startsWith("ZABF") || false;

  const genDetails = genericDetails.onlinePrograms.onlineProgramsConfigFields;
  const bgImage =
    pageDetails?.backgroundImage.localfile ??
    pageDetails?.backgroundImage?.sourceUrl;
  const skillsYouAcquire = courseIntroductionContent[0];
  const isCourseForMe = courseIntroductionContent[1];

  const sDGIcon = page.taxonomySDGIcon.nodes.map((icons) => {
    return {
      name: icons?.name,
      slug: icons?.slug,
      desc: icons?.description ?? ""
    };
  });

  const [getProgramTestimonials, setProgramTestimonials] = useState([]);

  const testimonialCollection = useProgramTestimonials({
    programId: programCode,
    currentBrand: theme?.currentBrand ?? "planet"
  });

  useEffect(() => {
    if (testimonialCollection)
      testimonialCollection
        .then((result) => {
          return result?.map((item) => ({
            ...item,
            reviewer: item?.title || "",
            rating: item?.rating / 2 || 3,
            review: item?.content || "",
            reviewDate: item?.date || "",
            sortByDate: Date.parse(item?.date) || new Date().getTime()
          }));
        })
        .then((res) => res?.sort(sortByDesc("sortByDate")))
        .then((res) => setProgramTestimonials(res))
        .catch((e) => console.error(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPrices = useCallback(() => {
    const currency =
      currentSiteMeta.currency ?? process.env.GATSBY_PROJECT_CURRENCY ?? "gbp";

    //console.debug("fetchPrices :: currentSiteMeta.currency ", currentSiteMeta);

    const controller = new AbortController();
    const signal = controller.signal;

    const getdates = async () => {
      const res = await fetch(
        `/api/salesforce/programdates?program_code=${programCode}`,
        { signal: signal }
      );
      const durations = await res.json();

      if (!durations) {
        // console.error("no durations", durations);
        return [];
      }
      // console.error("SF response  => ", durations);
      const today = new Date();

      const unformattedDepartureDates = durations
        ?.flatMap((durationInstanceDate) => ({
          departureDate: durationInstanceDate?.InstanceDate
        }))
        ?.filter(
          (startDate) =>
            new Date(startDate.departureDate).getTime() > today.getTime()
        )
        ?.flatMap((date) => date.departureDate)
        ?.sort()
        ?.shift();

      const nextInstanceStartDate =
        unformattedDepartureDates?.length > 0
          ? new Date(unformattedDepartureDates).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "long",
            year: "numeric"
          })
          : genDetails.enrollmentNextStartDate;

      const firstDuration = durations
        ?.flatMap((item) => {
          return {
            ...item?.ProductPrices?.flatMap((itemPrice) => {
              return {
                currency: itemPrice?.CurrencyIso?.toLowerCase(),
                price: itemPrice?.UnitPrice,
                weeklyPrice: itemPrice?.WeeklyPrice
              };
            })
              ?.filter((item) => item.currency === currency.toLowerCase())
              ?.shift(),
            duration: item?.DurationDesc,
            durationDays: item?.Duration,
            instanceDate: item?.InstanceDate,
            instanceFormattedDate: new Date(
              item?.InstanceDate
            ).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "long",
              year: "numeric"
            }),
            timeHours:
              item?.CourseCommitmentHours ?? `${item?.CommitmentHours} hours`
          };
        })
        ?.shift();

      //we need to update salesforce api to return the committment hours
      setNextCourseDate(nextInstanceStartDate);
      setCoursePrice({ ...firstDuration });
    };

    getdates().catch(console.error);

    return () => {
      controller.abort();
    };
  }, [currentSiteMeta, programCode, genDetails]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fetchPrices, [currentSiteMeta, programCode]);

  const {
    onlineProgramSectionCourseTitle,
    onlineProgramSectionExpertsTitle,
    onlineProgramSectionTestimonialsTitle
  } = genDetails || {};

  // console.debug("testimonials", getProgramTestimonials.length);

  return (
    <>
      <VIHero
        heroLocalImg={bgImage}
        heroContent={
          <OnlineHeroContent
            pageTitle={pageTitle}
            applyNow={applyNow}
            content={content}
            courseBrochure={courseBrochure}
            programCode={programCode}
            courseTitle={onlineProgramSectionCourseTitle}
          />
        }
        logoBoxDesc="This course was created in partnership with"
        logoBoxClass="!hidden md:!flex"
        isBushwiseProgram={isBushwiseProgram}
      />

      <div className="sticky-container">
        <CourseStickyBar
          courseBrochure={courseBrochure}
          nextDate={nextCourseDate}
          applyNow={applyNow}
          enquireNow={enquireNow}
          ref={stickyBarRef}
          programCode={programCode}
        />
        <CourseSection
          courseIntroduction={courseIntroduction}
          genDetails={genDetails}
          isCourseForMe={isCourseForMe}
          skillsYouAcquire={skillsYouAcquire}
          courseCertificateDescription={courseCertificateDescription}
          testimonials={getProgramTestimonials}
          programCode={programCode}
          coursePrice={coursePrice}
          isBushwiseProgram={isBushwiseProgram}
        />
        <div ref={curriculumRef}>
          <CourseCurriculum
            type={type}
            pageDetails={pageDetails}
            sDGIcon={sDGIcon}
            curriculumModules={curriculumModules}
            courseBundleRelations={courseBundleRelations}
          />
        </div>

        <CourseExpertList
          expertsData={courseInstructors}
          title={onlineProgramSectionExpertsTitle}
        />

        {getProgramTestimonials && (
          <CourseAlumniTestimonials
            testimonials={getProgramTestimonials}
            title={onlineProgramSectionTestimonialsTitle}
            centerSlides={getProgramTestimonials?.length >= 3 ? false : true}
          />
        )}

        {!isBushwiseProgram && <VIPartnerShip />}
        <CourseContact />
      </div>
    </>
  );
};

export default OnlineCoursePageComponent;
